import { observable, action, computed, reaction } from 'mobx';

import { leadsHelper } from '../helpers';
import { api } from '../services';
import lunr from 'lunr';
import { isFunction, flatten } from 'lodash';

import { filterStore } from 'air-filter-mobx-store';
import commonStore from "./commonStore";
import authStore from "./authStore";

import moment from 'moment';

class LeadsStoreWithAirFilter {
  @observable
  leads = [];
  @observable
  loading = false;

  @observable lunrResultAsObj;
  @observable sortingFunction = (lead1, lead2) => lead2.weight - lead1.weight;

  constructor(filterStore, commonStore, authStore) {
    this.filterStore = filterStore;
    this.commonStore = commonStore;
    this.authStore = authStore;
    reaction(
      // () => this.filterStore.globalFilterFunction,
      () => this.filterStore.globalFilterFunctionFromPage('leads'),
      fn => {
        const leads = this.leads.map(lead => {
          if (fn(lead)) {
            return { ...lead, hidden: false };
          } else {
            return { ...lead, hidden: true };
          }
        });
        this.setLeads(leads);
      }
    );
  }

  buildIndex(leads) {
    this.index = lunr(function() {
      this.field('client.fullName');
      this.field('car.plate');
      this.field('car.make.descrizione');
      this.field('car.model.descrizione');
      this.ref('id');
      leads.forEach(lead => {
        lead['client.fullName'] = lead.client.fullName;
        lead['car.plate'] = lead.car.plate;
        lead['car.make.descrizione'] = lead.car.make.descrizione;
        lead['car.model.descrizione'] = lead.car.model.descrizione;
        this.add(lead);
      });
    });
    return leads;
  }

  @action
  load() {
    this.leads = [];

    this.loading = true;
    const p1 = api('get all fidelity', this.commonStore.selectedDealers);
    const p2 = api('getLeads', this.commonStore.selectedDealers);

    return (
      Promise.all([p1, p2])
        .then(([fidelities, leads]) =>
          leads.map(lead => {
            lead.client.fidelityIndex = fidelities[lead.client.id] || null;
            lead.car.leadMaintenances = lead.car.maintenances
              .sort((a, b) => (moment(a.virtualDate).isAfter(b.virtualDate) ? 1 : moment(a.virtualDate).isBefore(b.virtualDate) ? -1 : 0))
              .slice(0, 1);
            return lead;
          })
        )
        //
        .then(leads => leads.map(leadsHelper.leadMapper))
        .then(leads => this.buildIndex(leads)) //build index for full text search
        .then(leads => leads.map(lead => ({ ...lead, weight: leadsHelper.weight(lead) })))
        .then(leads => {
          // const { globalFilterFunction } = this.filterStore;
          const globalFilterFunction = this.filterStore.globalFilterFunctionFromPage('leads');
          leads = leads.map(lead => {
            return globalFilterFunction instanceof Function ? (globalFilterFunction(lead) ? { ...lead, hidden: false } : { ...lead, hidden: true }) : lead;
          });
          return leads;
        })
        .then(leads => leads.sort((lead1, lead2) => lead2.weight - lead1.weight))
        .then(leads => {
          this.leads = leads;
          this.loading = false;
        })
    );
  }

  @action
  setLeads(leads) {
    this.leads = leads;
  }

  @action
  suspend(leadsToBeSuspended) {
    return api('suspendLeads', leadsToBeSuspended).then(response => {
      if (response.success === true) {
        this.leads = this.leads.map(lead => {
          if (leadsToBeSuspended.includes(lead.id)) {
            lead.status = 'suspended';
            return lead;
          } else {
            return lead;
          }
        });
      }
    });
  }

  @computed get dtcsRed() {
    return flatten(this.withDtcsRed.map(lead => lead.dtcs)).filter(dtc => dtc.severity === 'red');
  }

  @computed get dtcsOrange() {
    return flatten(this.withDtcsOrange.map(lead => lead.dtcs)).filter(dtc => dtc.severity === 'orange');
  }

  @computed get withDtcsRedPlusOrange() {
    return this.leads.filter(this.withDtcsRedPlusOrangeFilterFn);
  }

  @computed get withLowBattery() {
    return this.leads.filter(this.withLowBatteryFilterFn);
  }

  @computed get withAlarmsAndSafety() {
    return this.leads.filter(this.withAlarmsAndSafetyFilterFn);
  }

  @computed get withMaintenances() {
    return this.leads.filter(this.withMaintenancesFilterFn);
  }

  @computed get withCrash() {
    return this.leads.filter(this.withCrashFilterFn);
  }

  @computed get notified() {
    return this.leads.filter(this.notifiedFilterFn);
  }

  @computed get sent() {
    return this.leads.filter(this.sentFilterFn);
  }

  @computed get action() {
    return this.leads.filter(this.actionFilterFn);
  }

  @computed get booked() {
    return this.leads.filter(this.bookedFilterFn);
  }

  @computed get discarded() {
    return this.leads.filter(this.discardedFilterFn);
  }

  @computed get suspended() {
    return this.leads.filter(this.suspendedFilterFn);
  }

  @computed get withHelpRequests() {
    return this.leads.filter(this.withHelpRequestsFilterFn);
  }

  withDtcsRedFilterFn = lead => lead.dtcs.findIndex(dtc => dtc.severity === 'red') > -1;

  withDtcsOrangeFilterFn = lead => lead.dtcs.findIndex(dtc => dtc.severity === 'orange') > -1;

  withDtcsRedPlusOrangeFilterFn = lead => lead.dtcs.length > 0;
  // dtcs.length > 0;

  withLowBatteryFilterFn = lead => (lead.car.battery && lead.car.battery.value < 11000 && lead.car.battery.updatedAt && lead.car.battery.updatedAt != '');

  withAlarmsAndSafetyFilterFn = lead => lead.alarms.length > 0 || lead.alarms.findIndex(alarm => alarm.safety === true) > -1;

  withMaintenancesFilterFn = lead => parseInt(lead.daysToM) >= lead.dealer.maintenanceEligibleForLeadInDays * -1; // https://my-air.atlassian.net/browse/MYP-1589

  withCrashFilterFn = lead => lead.crashes.length > 0;

  notifiedFilterFn = lead => lead.status === 'notified';

  sentFilterFn = lead => lead.status === 'sent';

  actionFilterFn = lead => lead.status === 'action';

  bookedFilterFn = lead => lead.status === 'booked';

  discardedFilterFn = lead => lead.status === 'discarded';

  suspendedFilterFn = lead => lead.status === 'suspended';

  withHelpRequestsFilterFn = lead => lead.helpRequests.length > 0;

  searchQueryFilterFn = lead => {
    if (this.lunrResultAsObj && this.lunrResultAsObj[lead.id]) {
      return true;
    }
    return false;
  };

  @action
  setFullTextSearch(textSearch) {
    if (!textSearch) {
      this.lunrResultAsObj = null;
      return;
    }
    const lunrResult = this.index.search(textSearch);
    let lunrResultAsObj = {};
    lunrResult.forEach(match => {
      lunrResultAsObj[match.ref] = match;
    });

    this.lunrResultAsObj = lunrResultAsObj;
  }

  @action
  setSortingFunction(fn = (l1, l2) => l2.weight - l1.weight) {
    this.sortingFunction = fn;
  }

  search(textToSearch) {
    if (!textToSearch) {
      this.filterStore.remove('search.query');
      setTimeout(() => {
        this.filterStore.apply();
        this.setSortingFunction();
      }, 10);
      return;
    }
    this.filterStore.remove('search.query');
    this.setFullTextSearch(textToSearch);
    this.filterStore.add('search.query', this.searchQueryFilterFn);
    setTimeout(() => {
      this.filterStore.apply();
      const sortingFn = (lead1, lead2) => (this.lunrResultAsObj[lead2.id] || {}).score - (this.lunrResultAsObj[lead1.id] || {}).score;
      this.setSortingFunction(sortingFn);
    }, 10);
  }

  @computed get filtered() {
    return this.leads.filter(lead => !lead.hidden).sort(this.sortingFunction);
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };
}

export default new LeadsStoreWithAirFilter(filterStore, commonStore, authStore);
